import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowRight, BellRing, CalendarClock, CheckCircle2, Clock3, Users } from 'lucide-react'
import LandingMainHeader from './LandingMainHeader'

const toCanonicalSlug = (region) => `/${region}/soluciones/gestion-citas/agendalab`

const upsertMeta = (attributeName, key, content) => {
  if (!content) return

  const selector = `meta[${attributeName}="${key}"]`
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attributeName, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

const upsertCanonical = (href) => {
  let canonical = document.head.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', href)
}

export default function AgendaLabLanding() {
  const { region, category, product } = useParams()

  const normalizedRegion = (region || 'latam').toLowerCase()
  const normalizedCategory = (category || 'gestion-citas').toLowerCase()
  const normalizedProduct = (product || 'agendalab').toLowerCase()
  const isValidRoute = normalizedCategory === 'gestion-citas' && normalizedProduct === 'agendalab'
  const demoEntryUrl = '/api/auth/demo-entry?product=agendalab'

  useEffect(() => {
    if (!isValidRoute) return

    const canonicalPath = toCanonicalSlug(normalizedRegion)
    const canonicalUrl = `${window.location.origin}${canonicalPath}`
    const title = 'AgendaLab LATAM | AppEmprendedor'
    const description = 'AgendaLab centraliza citas, agenda del equipo, recordatorios y seguimiento de clientes para negocios de servicios.'

    document.title = title
    upsertCanonical(canonicalUrl)
    upsertMeta('name', 'description', description)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:url', canonicalUrl)
  }, [isValidRoute, normalizedRegion])

  if (!isValidRoute) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
        <div className="max-w-xl text-center">
          <h1 className="text-3xl font-display text-white">Landing no disponible</h1>
          <p className="mt-4 text-slate-300">La ruta solicitada no corresponde a la categoría o producto de AgendaLab.</p>
          <Link to="/latam/soluciones/gestion-citas/agendalab" className="inline-flex mt-6 px-5 py-3 rounded-xl bg-cyan-500 text-slate-950 font-semibold">
            Ir a la landing principal LATAM
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-[-180px] left-[-100px] h-[420px] w-[420px] rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute bottom-[-200px] right-[-80px] h-[480px] w-[480px] rounded-full bg-emerald-500/20 blur-3xl" />
      </div>

      <LandingMainHeader />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="inline-flex items-center px-3 py-1 rounded-full border border-cyan-300/30 text-cyan-200 text-xs tracking-wide uppercase">
          AgendaLab para LATAM
        </p>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl text-white leading-tight">
          Agenda, clientes y recordatorios en un solo sistema.
        </h1>
        <p className="mt-6 text-lg text-slate-300 max-w-3xl">
          AgendaLab de AppEmprendedor ordena tu operación de citas para que tu equipo atienda más y mejor: agenda por profesional,
          historial de clientes y seguimiento de servicios.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={demoEntryUrl}
            className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-950 hover:from-cyan-400 hover:to-blue-400 transition-all"
          >
            Entrar a la demo AgendaLab
          </a>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              icon: CalendarClock,
              title: 'Agenda centralizada',
              text: 'Visualiza horarios por equipo y servicio en una sola vista.'
            },
            {
              icon: BellRing,
              title: 'Recordatorios automaticos',
              text: 'Reduce ausencias con confirmaciones y avisos programados.'
            },
            {
              icon: Users,
              title: 'Clientes organizados',
              text: 'Consulta historial y preferencias para atender con contexto.'
            }
          ].map(({ icon: Icon, title, text }) => (
            <article key={title} className="rounded-2xl border border-slate-700 bg-slate-900/70 p-6">
              <Icon className="w-5 h-5 text-cyan-300" />
              <h2 className="mt-3 text-lg font-semibold text-white">{title}</h2>
              <p className="mt-2 text-sm text-slate-300">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-3xl text-white">Implementación rápida y enfoque en operación real</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4 text-sm text-slate-300">
            <p className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 flex items-start gap-2">
              <Clock3 className="w-4 h-4 text-cyan-300 mt-0.5" />
              Arranca en días con los flujos críticos de agenda y clientes.
            </p>
            <p className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5" />
              Escala por fases sin rehacer procesos ni perder trazabilidad.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl border border-slate-700 bg-slate-900/80 p-8">
          <h3 className="font-display text-2xl text-white">AgendaLab listo para tu operación</h3>
          <p className="mt-3 text-slate-300">
            Si quieres evaluar AgendaLab para tu negocio, entra al demo y revisa el flujo completo.
          </p>
          <a
            href={demoEntryUrl}
            className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-xl font-semibold bg-cyan-400 text-slate-950 hover:bg-cyan-300 transition-colors"
          >
            Entrar a la demo
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  )
}



